/*
<summary>
   This functional component is all about rendering the Add/Edit Tool Check Item Template popup and mapping form data to the API request.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useStore } from '../../../contexts/store-provider';
import { getUserId, getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import {
  IAddToolCheckItemTemplate,
  IToolCheckItemTemplateHeaderDetail,
} from '../../../models/forms/IToolCheckItemTemplate';
import { IFormModalProps } from '../../../models/forms/IForm';
import { AddToolCheckItemTemplateInitialState } from '../../../core/initialState/add-edit-tool-check-item-template-state';
import AddEditToolCheckItemTemplateForm from '../forms/add-edit-tool-check-item-form';

/**
* This functional component is used to render the Add/Edit Tool Check Item Template popup and build the API request from the form values.
* @param props: Component props containing the template identifier and the modal close handler
*/
const AddEditToolCheckItemTemplate = (props: IFormModalProps) => {
  const { toolCheckItemTemplateStore } = useStore();
  const {
    AddCheckItemTemplateService,
    UpdateCheckItemTemplateService,
    GetCheckItemTemplateByIdService,
    checkItemTemplateDetail,
  } = toolCheckItemTemplateStore;

  /**
   * This useEffect is used to load the check item template detail when editing, or reset the detail when adding a new one.
   */
  useEffect(() => {
    if (props.id > 0) {
      GetCheckItemTemplateByIdService(props.id);
    } else {
      toolCheckItemTemplateStore.resetDetail();
    }
  }, [props.id]);

  /**
  * This function is used to build the add/update API request from the form values and submit it to the store.
  * @param values: Add/Edit Tool Check Item Template form values
  */
  const onSubmitForm = (values: IAddToolCheckItemTemplate) => {
    const userId = Number(getUserId()) || 0;
    const tenantId = Number(getTenantIdFromLocalStorage()) || 0;

    if (props.id === -1) {
      // This is a create, so every header/check-item row is new and no per-row Action is needed.
      const toolCheckitemTemplateHeaderDetails: IToolCheckItemTemplateHeaderDetail[] =
        (values.Headers || []).flatMap((header, headerIndex) =>
          (header.CheckItems || []).map((item, itemIndex) => ({
            HeaderName: header.Title,
            HeaderOrder: headerIndex + 1,
            CheckitemText: item.CheckItemMessage,
            ItemOrder: itemIndex + 1,
            AllowTextEntry: !!item.IsChecked,
          }))
        );

      AddCheckItemTemplateService({
        TemplateName: values.CheckItemName,
        Details: values.Remarks,
        ToolCheckitemTemplateHeaderDetails: toolCheckitemTemplateHeaderDetails,
        CreatedBy: userId,
        TenantId: tenantId,
      });
      return;
    }

    // This is an update, so the API expects a flat diff of header/check-item rows, each tagged
    // with Action 1 (Add) / 2 (Update) / 3 (Delete), instead of the full nested tree.
    type IInitialCheckItemRow = {
      HeaderName: string;
      HeaderOrder: number;
      CheckitemText: string;
      ItemOrder: number;
      AllowTextEntry: boolean;
    };

    // This snapshots the rows as they were before editing, keyed by their saved Id, to diff against.
    const initialItemsById = new Map<number, IInitialCheckItemRow>();
    (checkItemTemplateDetail.Headers || []).forEach((header, headerIndex) => {
      (header.CheckItems || []).forEach((item, itemIndex) => {
        if (item.Id) {
          initialItemsById.set(item.Id, {
            HeaderName: header.Title,
            HeaderOrder: headerIndex + 1,
            CheckitemText: item.CheckItemMessage,
            ItemOrder: itemIndex + 1,
            AllowTextEntry: !!item.IsChecked,
          });
        }
      });
    });

    // This tracks the Ids seen while walking the current form below, used afterwards to find deletions.
    const currentItemIds = new Set<number>();
    const toolCheckitemTemplateHeaderDetails: IToolCheckItemTemplateHeaderDetail[] = [];

    (values.Headers || []).forEach((header, headerIndex) => {
      (header.CheckItems || []).forEach((item, itemIndex) => {
        const current: IInitialCheckItemRow = {
          HeaderName: header.Title,
          HeaderOrder: headerIndex + 1,
          CheckitemText: item.CheckItemMessage,
          ItemOrder: itemIndex + 1,
          AllowTextEntry: !!item.IsChecked,
        };

        if (!item.Id) {
          // This row has no saved Id yet, so it was added in this edit session.
          toolCheckitemTemplateHeaderDetails.push({
            Id: 0,
            CheckItemTemplateId: props.id,
            ...current,
            Action: 1,
            CreatedBy: userId,
          });
          return;
        }

        currentItemIds.add(item.Id);
        const initial = initialItemsById.get(item.Id);
        const isChanged =
          !initial ||
          initial.HeaderName !== current.HeaderName ||
          initial.HeaderOrder !== current.HeaderOrder ||
          initial.CheckitemText !== current.CheckitemText ||
          initial.ItemOrder !== current.ItemOrder ||
          initial.AllowTextEntry !== current.AllowTextEntry;

        // This only sends rows that actually changed against the initial snapshot; unchanged rows are omitted.
        if (isChanged) {
          toolCheckitemTemplateHeaderDetails.push({
            Id: item.Id,
            CheckItemTemplateId: props.id,
            ...current,
            Action: 2,
            UpdatedBy: userId,
          });
        }
      });
    });

    // This treats any saved row not seen while walking the current form as removed by the user, and marks it for deletion.
    initialItemsById.forEach((initial, id) => {
      if (!currentItemIds.has(id)) {
        toolCheckitemTemplateHeaderDetails.push({
          Id: id,
          CheckItemTemplateId: props.id,
          ...initial,
          Action: 3,
          UpdatedBy: userId,
        });
      }
    });

    UpdateCheckItemTemplateService({
      Id: props.id,
      TemplateName: values.CheckItemName,
      Details: values.Remarks,
      UpdatedBy: userId,
      ToolCheckitemTemplateHeaderDetails: toolCheckitemTemplateHeaderDetails,
      TenantId: tenantId,
    });
  };

  return (
    <AddEditToolCheckItemTemplateForm
      initialValues={
        props.id > 0
          ? checkItemTemplateDetail
          : AddToolCheckItemTemplateInitialState
      }
      id={props.id}
      submitHandler={onSubmitForm}
      onModalClosed={props.modalClosed}
      isLoading={
        toolCheckItemTemplateStore.addUpdateCheckItemTemplateState.inProgress ||
        toolCheckItemTemplateStore.getCheckItemTemplateByIdState.inProgress
      }
    />
  );
};

export default observer(AddEditToolCheckItemTemplate);
